import { Core as Cachemap } from '@cachemap/core';
import { init as webStorage } from '@cachemap/web-storage';
import { Search } from '@mui/icons-material';
import { IconButton, InputBase, Container as MuiContainer, type Theme, styled, useTheme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { useEnvs } from '@next-box/envs';
import { isString } from 'lodash-es';
import { useRouter, useSearchParams } from 'next/navigation.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchResultsAsYouType } from '#components/SearchResultsAsYouType/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { type ToolbarState } from '#enums/headerStates.ts';
import { Route } from '#enums/routes.ts';
import { tt } from '#i18n/index.ts';

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}));

const SearchContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexGrow: 1,
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
  },
}));

export type SearchBarProps = {
  searchFullScreen?: boolean;
  setActiveToolbarState: (state?: ToolbarState) => void;
  setSearchFullScreen: (fullScreen: boolean) => void;
  sx?: SystemStyleObject<Theme>;
};

export const SearchBar = ({ searchFullScreen, setActiveToolbarState, setSearchFullScreen, sx }: SearchBarProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const { getEnv } = useEnvs();
  const keyphrase = searchParams.get('keyphrase');
  const inputRef = useRef<HTMLInputElement>(undefined);
  const [inputValue, setInputValue] = useState<string>(isString(keyphrase) ? keyphrase : '');
  const [searchAsTypeValue, setSearchAsTypeValue] = useState<string>('');

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearchAsTypeValue(inputValue);
    }, 250);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [inputValue]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      inputRef.current = undefined;
    };
  }, []);

  const handleSearchTerm = () => {
    if (inputValue === getEnv('NEXT_PUBLIC_EXOTIC_PINK_PTARMIGAN_KEY')) {
      const cachemap = new Cachemap({
        backupStore: webStorage({ storageType: 'local' }),
        name: FeatureFlags.ExoticPinkPtarmigan,
        type: 'auth',
      });

      void cachemap.write('EXOTIC_PINK_PTARMIGAN_USER_KEY', inputValue);
      setInputValue('');
      setActiveToolbarState(undefined);
    } else {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('keyphrase', inputValue);
      router.push(`${Route.SearchResults}?${newSearchParams.toString()}`, { scroll: false });
    }

    if (searchFullScreen) {
      setSearchFullScreen(false);
    }
  };

  const memoSearchResultsAsYouType = useMemo(
    () => <SearchResultsAsYouType inputValue={searchAsTypeValue} setSearchFullScreen={setSearchFullScreen} />,
    [searchAsTypeValue, setSearchFullScreen],
  );

  return (
    <MuiContainer
      disableGutters
      maxWidth="xl"
      sx={{
        ...sx,
        height: searchFullScreen ? 'calc(100% - 56px)' : undefined,
        marginBottom: searchFullScreen ? 0 : undefined,
        paddingBottom: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
          height: searchFullScreen ? 'calc(100% - 64px)' : undefined,
          marginBottom: searchFullScreen ? 0 : undefined,
        },
      }}
    >
      <Container sx={{ height: searchFullScreen ? '100%' : undefined }}>
        <SearchContainer
          sx={{
            borderBottomLeftRadius: searchFullScreen ? 0 : undefined,
            borderBottomRightRadius: searchFullScreen ? 0 : undefined,
            flexGrow: 0,
          }}
        >
          <InputBase
            inputProps={{ 'aria-label': tt('header.searchAriaLabel') }}
            inputRef={inputRef}
            onChange={event => {
              setInputValue(event.target.value);

              if (event.target.value) {
                setSearchFullScreen(true);
              } else {
                setSearchFullScreen(false);
              }
            }}
            onKeyDown={event => {
              // This still needs support for devices
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              if ((event.code === 'Enter' || event.keyCode === 13) && !!inputValue) {
                handleSearchTerm();
              }

              // This still needs support for devices
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              if (event.code === 'Escape' || event.keyCode === 27) {
                setActiveToolbarState(undefined);

                if (searchFullScreen) {
                  setSearchFullScreen(false);
                }
              }
            }}
            placeholder={tt('header.searchPlaceholder')}
            sx={{
              flexGrow: 1,
            }}
            value={inputValue}
          />
          <IconButton
            onClick={() => {
              if (inputValue) {
                handleSearchTerm();
              }
            }}
            sx={{
              position: 'absolute',
              right: theme.spacing(0.5),
              top: theme.spacing(0.5),
              [theme.breakpoints.down('sm')]: {
                top: 0,
              },
            }}
          >
            <Search />
          </IconButton>
        </SearchContainer>
        {searchFullScreen ? memoSearchResultsAsYouType : undefined}
      </Container>
    </MuiContainer>
  );
};
